import type { Affiliation } from './semicolon-csv'
import { isAffiliationTooLong } from '~/helpers/users/affiliation'
import { isValidUsername, normalizeUsername } from '~/helpers/users/username'
import { affiliationOf, cellOf, readSemicolonCsv } from './semicolon-csv'

/**
 * Colonnes du CSV d'apprenants en pseudo (#271), séparateur `;`. Seul `pseudo`
 * est obligatoire (`societe` et `service` aussi facultatives, #277) : un établissement de santé peut ne saisir aucun nom. Les
 * mots de passe sont générés par l'API, jamais lus dans un fichier.
 */
export const USERNAME_CSV_HEADERS = ['pseudo', 'prenom', 'nom', 'groupe', 'societe', 'service'] as const

export type UsernameRow = Affiliation & { username: string, firstName?: string, lastName?: string, group?: string }

export type UsernameIssue = {
  /** Rang de la ligne dans les données (1 = première ligne après l'en-tête). */
  line: number
  username: string
  reason: 'MISSING_FIELDS' | 'INVALID_USERNAME' | 'DUPLICATE_IN_FILE' | 'AFFILIATION_TOO_LONG'
}

export type CheckedUsernameRow = { line: number, row: UsernameRow }

export type UsernameParseResult
  = | { ok: true, valid: CheckedUsernameRow[], issues: UsernameIssue[] }
    | { ok: false, error: 'empty' | 'badHeader' }

/**
 * Lit le fichier et rejoue les contrôles de l'API qui ne demandent pas la base
 * (pseudo manquant, invalide, en double). Un pseudo déjà pris dans l'org ne se
 * voit qu'au retour de l'import, dans son rapport.
 */
export function parseUsernameCsv(text: string): UsernameParseResult {
  const csv = readSemicolonCsv(text)
  if (!csv) return { ok: false, error: 'empty' }
  if (!csv.header.includes('pseudo')) return { ok: false, error: 'badHeader' }

  const valid: CheckedUsernameRow[] = []
  const issues: UsernameIssue[] = []
  const seen = new Set<string>()
  csv.rows.forEach((cells, index) => {
    const cell = (name: string) => cellOf(csv, cells, name)
    const line = index + 1
    const username = normalizeUsername(cell('pseudo'))
    const reason = !username
      ? 'MISSING_FIELDS'
      : !isValidUsername(username) ? 'INVALID_USERNAME' : seen.has(username) ? 'DUPLICATE_IN_FILE' : null
    if (reason) {
      issues.push({ line, username, reason })
      return
    }
    seen.add(username)
    const affiliation = affiliationOf(csv, cells)
    if (isAffiliationTooLong(affiliation)) {
      issues.push({ line, username, reason: 'AFFILIATION_TOO_LONG' })
      return
    }
    const [firstName, lastName, group] = [cell('prenom'), cell('nom'), cell('groupe')]
    valid.push({ line, row: { username, ...(firstName && { firstName }), ...(lastName && { lastName }), ...(group && { group }), ...affiliation } })
  })
  return { ok: true, valid, issues }
}
